import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { hasRole } from '../utils/roles';

const SERVICES = [
  { id: 'scuore-card', label: 'Scuore Card' },
  { id: 'scuore-quality', label: 'Scuore Quality' },
];

export const UserManagement = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const searchTimeoutRef = useRef(null);

  const loadUsers = useCallback(
    async (currentPage = 0, currentRowsPerPage = 10, searchFilter = '') => {
      try {
        setLoading(true);
        const filter = searchFilter ? { search: searchFilter } : {};
        const { data, total } = await dataProvider.getList('members', {
          pagination: { page: currentPage + 1, perPage: currentRowsPerPage },
          sort: { field: 'first_name', order: 'ASC' },
          filter: filter,
        });
        setUsers(data);
        setTotal(total);
      } catch (error) {
        console.error('Error loading users:', error);
        notify('Error al cargar usuarios', { type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [dataProvider, notify]
  );

  useEffect(() => {
    loadUsers(page, rowsPerPage, debouncedSearchTerm);
  }, [loadUsers, page, rowsPerPage, debouncedSearchTerm]);

  // Cleanup timeout on unmount
  useEffect(() => {
    const timeoutId = searchTimeoutRef.current;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const updateService = async (id, services) => {
    try {
      await dataProvider.update('members', {
        id,
        data: { services: services },
      });
      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === id ? { ...user, services: services } : user
        )
      );
      notify('Servicios actualizados correctamente', { type: 'success' });
    } catch (error) {
      console.error('Error updating user services:', error);
      notify('Error al actualizar servicios', { type: 'error' });
    }
  };

  // Filtrar usuarios basado en búsqueda (ahora se hace en el servidor)
  const displayedUsers = users;

  // Funciones para selección de usuarios
  const handleSelectUser = userId => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const allUserIds = displayedUsers.map(user => user.id);
    setSelectedUsers(prev =>
      prev.length === allUserIds.length ? [] : allUserIds
    );
  };

  const isAllSelected =
    displayedUsers.length > 0 && selectedUsers.length === displayedUsers.length;
  const isIndeterminate =
    selectedUsers.length > 0 && selectedUsers.length < displayedUsers.length;

  // Función para asignar servicios en bulk
  const handleBulkServiceAssignment = async (serviceId, assign) => {
    if (selectedUsers.length === 0) {
      notify('Selecciona al menos un usuario', { type: 'warning' });
      return;
    }

    try {
      const updates = selectedUsers.map(async userId => {
        const user = displayedUsers.find(u => u.id === userId);
        if (!user) return;

        const currentServices = user.services || [];
        const updatedServices = assign
          ? [...new Set([...currentServices, serviceId])]
          : currentServices.filter(s => s !== serviceId);

        await updateService(userId, updatedServices);
      });

      await Promise.all(updates);

      notify(
        `Servicio ${assign ? 'asignado' : 'removido'} a ${selectedUsers.length} usuario(s)`,
        { type: 'success' }
      );
    } catch (error) {
      console.error('Error updating bulk services:', error);
      notify('Error al actualizar servicios', { type: 'error' });
    }
  };

  const handleServiceToggle = async (
    userId,
    serviceId,
    currentServices = []
  ) => {
    const updatedServices = currentServices.includes(serviceId)
      ? currentServices.filter(s => s !== serviceId)
      : [...currentServices, serviceId];

    await updateService(userId, updatedServices);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = event => {
    const value = event.target.value;
    setInputValue(value);
    setPage(0); // Reset to first page when searching

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 3000); // 3 seconds delay
  };

  if (!hasRole(['admin'])) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          No tienes permisos para acceder a esta página
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', padding: '1rem' }}>
      <Card
        elevation={0}
        sx={{ background: '#fff', padding: '20px', marginTop: 2 }}
      >
        <CardHeader
          title="Gestión de Usuarios"
          subheader="Administra los servicios a los que pueden acceder los usuarios"
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2, px: 2 }}>
          <TextField
            label="Buscar usuarios"
            variant="outlined"
            size="small"
            value={inputValue}
            onChange={handleSearchChange}
            sx={{ minWidth: 300 }}
            placeholder="Buscar por nombre o email..."
          />
          {selectedUsers.length > 0 && (
            <Typography
              variant="body2"
              sx={{ alignSelf: 'center', color: 'primary.main' }}
            >
              {selectedUsers.length} usuario(s) seleccionado(s)
            </Typography>
          )}
        </Box>
        {selectedUsers.length > 0 && (
          <Box
            sx={{ display: 'flex', gap: 2, mb: 2, px: 2, alignItems: 'center' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Asignar servicios a seleccionados:
            </Typography>
            {SERVICES.map(service => (
              <Box
                key={service.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={() => handleBulkServiceAssignment(service.id, true)}
                >
                  + {service.label}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => handleBulkServiceAssignment(service.id, false)}
                >
                  - {service.label}
                </Button>
              </Box>
            ))}
          </Box>
        )}
        <CardContent>
          {loading ? (
            <Typography>Cargando usuarios...</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 50 }}>
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rol</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Servicios Permitidos
                    </TableCell>
                    {SERVICES.map(service => (
                      <TableCell
                        key={service.id}
                        sx={{ fontWeight: 600, textAlign: 'center' }}
                      >
                        {service.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedUsers.map(user => {
                    const allowedServices = user.services || [];
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => handleSelectUser(user.id)}
                          />
                        </TableCell>
                        <TableCell>
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {user.rolegerente ? (
                            <Chip
                              label="Gerente"
                              color="primary"
                              size="small"
                            />
                          ) : (
                            <Chip label="Usuario" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            {allowedServices.map(serviceId => {
                              const service = SERVICES.find(
                                s => s.id === serviceId
                              );
                              return service ? (
                                <Chip
                                  key={serviceId}
                                  label={service.label}
                                  size="small"
                                  color="success"
                                />
                              ) : null;
                            })}
                            {allowedServices.length === 0 && (
                              <Typography variant="body2" color="textSecondary">
                                Ninguno
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {SERVICES.map(service => (
                          <TableCell
                            key={service.id}
                            sx={{ textAlign: 'center' }}
                          >
                            <Checkbox
                              checked={allowedServices.includes(service.id)}
                              onChange={() =>
                                handleServiceToggle(
                                  user.id,
                                  service.id,
                                  allowedServices
                                )
                              }
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
